import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringToolsContainer } from "../../core/StringToolsContainer";
import { SequenceState } from "../../models/slider/SequenceState";
import { StringObject } from "../../objects/StringObject";

/**
 * Module that manages sequence states for elements based on slider step events.
 * Updates element classes (ACTIVE, HIDE, DISABLE) according to the current sequence position.
 */
export class StringSequence extends StringModule {
  /**
   * Stores the current active step index for each slider.
   */
  private activeStepForSlides: Map<string, number> = new Map();

  /**
   * Stores the last active step index for each slider to hide the previous element.
   */
  private hideStepForSlides: Map<string, number> = new Map();
  /**
   * @param context StringContext instance for dependency injection.
   */

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "sequence";

    this.attributesToMap = [
      ...this.attributesToMap,
      { key: "sequence", type: "string", fallback: "string[0]" },
    ];
  }

  /**
   * Subscribes to slider step events and updates element states:
   * - Sets ACTIVE for the current element.
   * - Sets HIDE for the previously active element.
   * - Sets DISABLE for all others.
   */
  onInit(): void {
    super.onInit();
    this.events.on<{ slider: string; step: number }>(
      "sequence",
      ({ slider, step }) => {
        this.objectsOnPage.forEach((object) => {
          const sequence = object.getProperty<string>("sequence");
          if (
            sequence == `${slider}[${this.hideStepForSlides.get(slider)}]` &&
            sequence != `${slider}[${step}]`
          ) {
            this.setSequenceState(object, SequenceState.DISABLE);
          }
          if (
            sequence == `${slider}[${this.activeStepForSlides.get(slider)}]`
          ) {
            this.setSequenceState(object, SequenceState.HIDE);
          }
          if (sequence == `${slider}[${step}]`) {
            this.setSequenceState(object, SequenceState.ACTIVE);
          }
        });
        let activeIndex = this.activeStepForSlides.get(slider);
        if (activeIndex != undefined) {
          this.hideStepForSlides.set(slider, activeIndex);
        }
        this.activeStepForSlides.set(slider, step);
      }
    );
  }

  /**
   * Sets initial state DISABLE for a newly connected element.
   * @param object The connected StringObject.
   */
  onObjectConnected(object: StringObject): void {
    super.onObjectConnected(object);
    this.setSequenceState(object, SequenceState.DISABLE);
  }

  /**
   * Updates the element's class according to the given SequenceState.
   * Removes all possible sequence state classes before applying the new one.
   * @param object The target StringObject.
   * @param state The state to set (ACTIVE, HIDE, DISABLE).
   */
  private setSequenceState(object: StringObject, state: SequenceState) {
    requestAnimationFrame(() => {
      object.htmlElement.classList.remove(...Object.values(SequenceState));
      object.htmlElement.classList.add(state);
    });
  }
}
